import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Stack,
  Button,
  TextField,
  MenuItem,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  BackupTable as ExportIcon,
  Search as SearchIcon,
  FilterAlt as FilterIcon,
  ClearAll as ClearIcon,
  ArrowDownward as SortDownIcon,
  ArrowUpward as SortUpIcon,
} from "@mui/icons-material";
import TopNav from "../components/topnav";
import EditModal from "../components/billing/EditModal";
import DeleteModal from "../components/billing/DeleteModal";
import SummaryModal from "../components/billing/SummaryModal";
import PatientBillingTable from "../components/billing/PatientBillingTable";
import FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { DateRangePicker } from "rsuite";
import { toast } from "react-toastify";
import StyledMenu from "../components/StyledMenu";
import { secureApi } from "../config";
import { Context } from "../context";
import {
  PersonOutline as PersonIcon,

} from "@mui/icons-material";

// Styled components for a more modern UI
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  overflow: "hidden",
  transition: "all 0.3s ease",
  marginBottom: theme.spacing(3),
  border: "1px solid #e0e0e0",
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 8,
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
  "& .MuiTableCell-root": {
    padding: "16px 12px",
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  "& .MuiTableCell-head": {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme, status }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: "#f5f9ff",
  },
  ...(status === "DENIED" && {
    backgroundColor: "#fff8f8",
    "&:hover": {
      backgroundColor: "#ffefef",
    },
  }),
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 500,
  borderRadius: 4,
  ...(status === "PAID" && {
    backgroundColor: "#e1f5e1",
    color: "#2e7d32",
  }),
  ...(status === "DENIED" && {
    backgroundColor: "#ffebee",
    color: "#c62828",
  }),
  ...(status === "PENDING" && {
    backgroundColor: "#fff8e1",
    color: "#f57c00",
  }),
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  textTransform: "none",
  padding: "8px 16px",
  fontWeight: 500,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
  },
}));

const Billing = () => {
  const { setLoggedIn } = useContext(Context);
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [patient, setPatient] = useState({});
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [billing, setBilling] = useState({});
  const [searchStr, setSearchStr] = useState("");
  const [filterDates, setFilterDates] = useState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const [sortField, setSortField] = useState("date_billed");
  const [sortDirection, setSortDirection] = useState("desc");
  const open = Boolean(anchorEl);

  const handleEditModalOpen = (billing) => {
    setBilling(billing);
    setEditModalOpen(true);
  };

  const handleDeleteModalOpen = (billing) => {
    setBilling(billing);
    setDeleteModalOpen(true);
  };

  const handleSummaryModalOpen = (patient) => {
    setPatient(patient);
    setSummaryModalOpen(true);
  };

  const handleEditModalClose = (_, reason) => {
    if (reason !== "backdropClick") {
      setEditModalOpen(false);
    }
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
  };

  const handleSummaryModalClose = () => {
    setSummaryModalOpen(false);
  };

  const handleDeleteBilling = (billingID) => {
    const newPatients = [...patients];
    for (let i = 0; i < newPatients.length; i++) {
      if (newPatients[i].billings) {
        // Find and remove the specific billing
        const billingIndex = newPatients[i].billings.findIndex(
          (b) => b._id === billingID
        );
        if (billingIndex !== -1) {
          // Remove the billing using splice
          newPatients[i].billings.splice(billingIndex, 1);
          setPatients(newPatients);
          return;
        }
      }
    }
  };

  const handleUpdateBilling = (billing) => {
    const newPatients = [...patients];
    for (let i = 0; i < newPatients.length; i++) {
      if (newPatients[i].billings) {
        // Find and update the specific billing
        const billingIndex = newPatients[i].billings.findIndex(
          (b) => b._id === billing._id
        );
        if (billingIndex !== -1) {
          // Update the billing at the found index
          newPatients[i].billings[billingIndex] = billing;
          setPatients(newPatients);
          return;
        }
      }
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // If already sorting by this field, toggle direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If sorting by a new field, set it and default to descending
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const downloadEmrBillingReport = () => {
    setAnchorEl(null);

    // Check if there are any patients
    if (!filteredPatients?.length) {
      toast.warn("No available patients to export");
      return;
    }

    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";

    // Convert data with headers to sheet
    const dataWithHeaders = [];

    // Process each patient's notes
    filteredPatients.forEach((patient) => {
      if (patient.notes) {
        patient.notes.forEach((note) => {
          const rowData = {
            "Patient ID": patient.identifier || "",
            "Date Of Service": note.dateOfService || note.dateOfProcedure || "",
            "First Name": patient.first_name || "",
            "Last Name": patient.last_name || "",
            CPTs: note.cptCodes
              ? note.cptCodes
                  .map((item) => item?.split(" - ")?.[0] || item)
                  .join(", ")
              : note.CPTcode || "",
            "DX (ICD Code)": note.icd10codes?.join(", ") || "",
            Authorization: note.summary?.auth || "",
            "Authorization Validity Dates": note.summary?.validity || "",
            Provider: "Dr Alex",
            Location: patient.location || "",
            "Chart#": patient.chart || "",
            Insurance: patient.insurance || "",
            "Insurance ID (Policy number)":
              patient.insurance_policy_number || "",
          };
          dataWithHeaders.push(rowData);
        });
      }
    });

    // Convert data with headers to sheet
    const ws = XLSX.utils.json_to_sheet(dataWithHeaders);

    // Create workbook and add sheet with data
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "EMR Billing Report");

    // Write workbook to buffer in xlsx format
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    // Convert buffer to Blob with specified MIME type
    const data = new Blob([excelBuffer], { type: fileType });

    // Save data as a file with specified filename
    FileSaver.saveAs(
      data,
      `EMR_Billing_Report_${
        new Date().toISOString().split("T")[0]
      }${fileExtension}`
    );
  };

  const downloadDemographicsReport = () => {
    setAnchorEl(null);

    // Check if there are any patients
    if (!filteredPatients?.length) {
      toast.warn("No available patients to export");
      return;
    }

    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";

    // Convert data with headers to sheet
    const dataWithHeaders = [];

    // Process each patient's billings
    filteredPatients.forEach((patient) => {
      const rowData = {
        "Patient ID": patient.identifier || "",
        "First Name": patient.first_name || "",
        "Last Name": patient.last_name || "",
        DOB: patient.date_of_birth || "",
        Gender: patient.gender || "",
        "Home#": patient.contactInfo?.home_phone || "",
        "Cell#": patient.contactInfo?.contact_number || "",
        Email: patient.contactInfo?.email_address || "",
        "Primary Address": patient.contactInfo?.primary_address || "",
        Insurance: patient.insurance || "",
        "Insurance ID (Policy number)": patient.insurance_policy_number || "",
        "Primary Insurance Relation": patient.primary_insurance_relation || "",
      };
      dataWithHeaders.push(rowData);
    });

    // Convert data with headers to sheet
    const ws = XLSX.utils.json_to_sheet(dataWithHeaders);

    // Create workbook and add sheet with data
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "EMR Billing Report");

    // Write workbook to buffer in xlsx format
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    // Convert buffer to Blob with specified MIME type
    const data = new Blob([excelBuffer], { type: fileType });

    // Save data as a file with specified filename
    FileSaver.saveAs(
      data,
      `Demographics_Report_${
        new Date().toISOString().split("T")[0]
      }${fileExtension}`
    );
  };

  const clearFilters = () => {
    setSearchStr("");
    setFilterDates(null);
    setIsFiltering(false);
  };

  // Function to render sortable column headers
  const renderSortableHeader = (label, field) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
      }}
      onClick={() => handleSort(field)}
    >
      {label}
      {sortField === field ? (
        <Box component="span" sx={{ ml: 0.5 }}>
          {sortDirection === "asc" ? (
            <SortUpIcon fontSize="small" />
          ) : (
            <SortDownIcon fontSize="small" />
          )}
        </Box>
      ) : null}
    </Box>
  );



  useEffect(() => {
    secureApi(window.localStorage.getItem("alex-med-token"))
      .get(`/patients_billings`)
      .then((response) => {
        const { patients } = response.data;
        setPatients(patients);
      })
      .catch((e) => {
        console.log(e);
        if (e.response && e.response.status === 401) {
          toast.warn("Session has been expired. You need to login again!");
          setLoggedIn(false);
          navigate("/login");
        } else {
          toast.error(e.response.data.message);
        }
      });
  }, []);

  useEffect(() => {
    setIsFiltering(!!searchStr || !!filterDates);
    
    if (searchStr || filterDates) {
      // Create a deep copy of patients to avoid mutating the original data
      const patientsToFilter = patients.map((patient) => ({
        ...patient,
        notes: patient.notes ? [...patient.notes] : [],
        billings: patient.billings ? [...patient.billings] : [],
      }));

      const filtered = patientsToFilter
        .map((patient) => {
          const filteredBillings = patient.billings?.filter((billing) => {
            // Date filtering
            let matchesDates = true;
            if (filterDates?.[0] && filterDates?.[1]) {
              const startDate = new Date(filterDates[0]);
              const endDate = new Date(filterDates[1]);

              if (billing.date_of_service) {
                const [month, day, year] = billing.date_of_service.split("/");
                const serviceDate = new Date(year, month - 1, day);
                matchesDates =
                  serviceDate >= startDate && serviceDate <= endDate;
              } else {
                matchesDates = false;
              }
            }

            return matchesDates;
          });

          const filteredNotes = patient.notes?.filter((note) => {
            // Date filtering
            let matchesDates = true;
            if (filterDates?.[0] && filterDates?.[1]) {
              const startDate = new Date(filterDates[0]);
              const endDate = new Date(filterDates[1]);

              const dateOfService = note.dateOfService || note.dateOfProcedure;
              if (dateOfService) {
                const [month, day, year] = dateOfService.split("/");
                const serviceDate = new Date(year, month - 1, day);
                matchesDates =
                  serviceDate >= startDate && serviceDate <= endDate;
              } else {
                matchesDates = false;
              }
            }

            // Search filtering
            const searchTerm = searchStr?.toLowerCase().trim() || "";
            const matchesSearch =
              !searchStr ||
              [patient.insurance, patient.first_name, patient.last_name].some(
                (field) => field?.toLowerCase().includes(searchTerm)
              );

            return matchesSearch && matchesDates;
          });

          return {
            ...patient,
            notes: filteredNotes,
            billings: filteredBillings,
          };
        })
        .filter((patient) => patient.notes?.length > 0);

      setFilteredPatients(filtered);
    } else {
      setFilteredPatients(patients);
    }
  }, [searchStr, filterDates, patients]);

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <>
      <TopNav item="billing" />
      <Box sx={{ padding: 4, maxWidth: 1400, margin: "0 auto" }}>
      <Typography variant="h5" component="h1" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                      <PersonIcon sx={{ mr: 1 }} />
                      Billing Information
        </Typography>


        
  
        {/* Search & Export Section */}
        <StyledCard>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <StyledTextField
                  fullWidth
                  size="small"
                  label="Search Patients/Insurance"
                  variant="outlined"
                  value={searchStr}
                  onChange={(e) => setSearchStr(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DateRangePicker
                  format="MM/dd/yyyy"
                  character=" - "
                  placeholder="Filter by Service Date"
                  size="md"
                  block
                  value={filterDates}
                  onChange={(value) => setFilterDates(value)}
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  {isFiltering && (
                    <Tooltip title="Clear filters">
                      <Button
                        size="small"
                        startIcon={<ClearIcon />}
                        onClick={clearFilters}
                        variant="outlined"
                      >
                        Clear
                      </Button>
                    </Tooltip>
                  )}
                  <StyledButton
                    aria-controls={open ? "export-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    variant="contained"
                    disableElevation
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    endIcon={<ExportIcon />}
                    color="primary"
                  >
                    Export
                  </StyledButton>
                  <StyledMenu
                    MenuListProps={{
                      "aria-labelledby": "export-button",
                    }}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                  >
                    <MenuItem onClick={downloadEmrBillingReport}>
                      EMR Billing Report
                    </MenuItem>
                    <MenuItem onClick={downloadDemographicsReport}>
                      Demographics Report
                    </MenuItem>
                  </StyledMenu>
                </Box>
              </Grid>
            </Grid>
            
            {isFiltering && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Active filters:
                </Typography>
                <Box sx={{ display: 'flex', mt: 1, flexWrap: 'wrap', gap: 1 }}>
                  {searchStr && (
                    <Chip 
                      size="small" 
                      label={`Search: "${searchStr}"`} 
                      color="primary" 
                      variant="outlined" 
                      onDelete={() => setSearchStr("")}
                    />
                  )}
                  {filterDates && (
                    <Chip 
                      size="small" 
                      icon={<FilterIcon fontSize="small" />}
                      label="Date filter active" 
                      color="primary" 
                      variant="outlined" 
                      onDelete={() => setFilterDates(null)}
                    />
                  )}
                </Box>
              </Box>
            )}
          </CardContent>
        </StyledCard>
        
        {/* Original PatientBillingTable (commented out for now as we're showing the direct billing records) */}
        
        <StyledCard sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Patient Billing Records
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filteredPatients.length} {filteredPatients.length === 1 ? 'patient' : 'patients'} found
              </Typography>
            </Box>
            
            <StyledTableContainer>
              <Table aria-label="patient billing table">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Chart #</TableCell>
                    <TableCell>Patient</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPatients.length > 0 ? (
                    filteredPatients.map((patient, index) => (
                      <PatientBillingTable
                        patient={patient}
                        key={index}
                        handleDeleteModalOpen={handleDeleteModalOpen}
                        handleEditModalOpen={handleEditModalOpen}
                        handleSummaryModalOpen={handleSummaryModalOpen}
                      />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" color="text.secondary">
                            No Billing Information Available
                          </Typography>
                          {isFiltering && (
                            <Button
                              size="small"
                              variant="text"
                              onClick={clearFilters}
                              startIcon={<ClearIcon />}
                            >
                              Clear Filters
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </CardContent>
        </StyledCard>
       
      </Box>
      
      {/* Modals */}
      <DeleteModal
        open={deleteModalOpen}
        billing={billing}
        handleClose={handleDeleteModalClose}
        handleDeleteBilling={handleDeleteBilling}
      />
      <EditModal
        open={editModalOpen}
        billing={billing}
        setBilling={setBilling}
        handleClose={handleEditModalClose}
        handleUpdateBilling={handleUpdateBilling}
      />
      <SummaryModal
        open={summaryModalOpen}
        patient={patient}
        filterDates={filterDates}
        handleClose={handleSummaryModalClose}
      />
    </>
  );
};

export default Billing;